/**
 * @file `useUpdateProfileMutation` — POST /api/account/update; on
 *       success, drop the returned learner into `qkMe`.
 * @module @labcraft/lab/ui/hooks/use-update-profile
 *
 * Mirrors admin's `useSignoutMutation` pattern: imperative
 * `setQueryData` over invalidation, so the chrome that reads
 * `useMe()` sees the freshly-updated `displayName` /
 * `profileCompletedAt` synchronously on the next render — no flash
 * of the old name while a refetch is in flight.
 *
 * Convention: hooks live in this directory and never call
 * `useMutation` outside it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { AccountLearner } from '../../client/account.ts'
import { qkMe } from './query_keys.ts'
import type { MeData } from './use_me.ts'

/** Mutation input — just the display-name change today. */
export type UpdateProfileArgs = {
  displayName: string
}

/**
 * Update the learner-mutable profile fields. On success the mutation
 * writes the returned learner into `qkMe` so subsequent reads see
 * the freshly-stamped values without a `/account/me` round-trip.
 */
export function useUpdateProfileMutation() {
  const client = useClient<Lab>()
  const queryClient = useQueryClient()
  return useMutation<AccountLearner, Error, UpdateProfileArgs>({
    mutationFn: args => client.account.updateProfile(args),
    onSuccess: learner => {
      queryClient.setQueryData<MeData>(qkMe, { learner })
    },
  })
}
